// Imported directly rather than through the `utilz` barrel, for the same reason
// auditEventCatalog.ts does it: the barrel pulls in DOM-dependent modules that
// break this file's unit tests in Vitest's node environment.
import { dayjs } from "utilz/dayjs";
import type { AuditFormatOptions } from "./auditEventCatalog";

/**
 * Below this, the relative phrasing is more useful than a clock time.
 *
 * One hour, not a day: past the first hour "7 hours ago" is harder to place
 * against the rest of the ticket than "9:00 am" is, so recency only wins while
 * the activity is still effectively live.
 */
const RELATIVE_CUTOFF_HOURS = 1;

/**
 * The timeline's right-hand timestamp.
 *
 * Recency is what a reader wants for the last hour — "5 minutes ago" answers
 * "is this still happening?" in a way a clock time doesn't. Past that every
 * entry gets an explicit date and time: no `Today` / `Yesterday` prefixes, so a
 * long timeline reads as one consistent column instead of switching vocabulary
 * partway down.
 *
 * | Age               | Format           | Example                |
 * |-------------------|------------------|------------------------|
 * | under 1 h         | relative         | `5 minutes ago`        |
 * | earlier this year | `MMMM D, h:mm a` | `July 5, 11:15 am`     |
 * | previous years    | `MMMM D, YYYY, …`| `July 5, 2025, 11:15 am` |
 *
 * ⚠️ The date is rendered in the **resource timezone**, and the year comparison
 * below uses `.format()` for the same reason: `isTodayTz` / `.startOf("day")`
 * and friends resolve boundaries in the **machine's** zone (on an IST host,
 * `dayjs().tz("UTC").startOf("day")` is 05:30 off), which would date one entry
 * differently for a viewer in Chennai than for a colleague in London.
 */
export const formatAuditTimestamp = (
  iso: string,
  options: Pick<AuditFormatOptions, "timezone">,
): string => {
  const at = dayjs(iso).tz(options.timezone);
  if (!at.isValid()) return "";

  const now = dayjs().tz(options.timezone);
  const hoursElapsed = now.diff(at, "hour", true);

  // Guarded to the past: a clock-skewed future timestamp would otherwise render
  // "in 2 hours", so those fall through to an absolute format instead.
  if (hoursElapsed >= 0 && hoursElapsed < RELATIVE_CUTOFF_HOURS) {
    return at.fromNow();
  }

  // The year appears only once the entry falls outside the current one — a bare
  // "July 5" on a two-year-old ticket reads as this year.
  return at.format("YYYY") === now.format("YYYY")
    ? at.format("MMMM D, h:mm a")
    : at.format("MMMM D, YYYY, h:mm a");
};
